import React from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { useFieldArray, useFormContext, type Control } from 'react-hook-form'
import { ApiAutoComplete } from '../../../../../components/FormElements/ApiAutoComplete'
import GenericButton from '../../../../../components/GenericButton'
import { ButtonType, ButtonSize } from '../../../../../components/GenericButton/styles'
import { black, grey, red, shade } from '../../../../../colors'
import { ReactComponent as Trash } from '../../../../../assets/trash.svg'
import GenericAutocomplete from '../../../../../components/FormElements/FormAutoComplete'
import { useParams, useSearchParams, useMatch } from 'react-router-dom'
import CustomTooltip from '../../../../../components/CustomTooltip'
import { type DisplaySettings } from '../../../types'
import { getCurrentRole } from '../../../../../helpers/roleHelpers'
import { useUserProfile } from '../../../../../hooks/useUserProfile'

interface ComparisonSearchOption {
  divisionId?: string
  sectionId?: string
  userId?: string
  id?: string
  title?: string
  abbreviation?: string | null
}

const COMPARISONS = 'comparisons' as const

interface ComparisonsSectionProps {
  name: typeof COMPARISONS
  control: Control<DisplaySettings>
}

const ComparisonsSection: React.FC<ComparisonsSectionProps> = ({ name, control }) => {
  const theme = useTheme()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const seriesId = searchParams.get('projectId')
  const termId = searchParams.get('termId')
  const objectId = searchParams.get('objectId')
  // On the instructor route, `id` is the instructor's user UUID — not a division UUID.
  // Only fall back to `id` on division report pages where `id` is a division UUID.
  const isInstructorRoute = useMatch('/instructor/surveys/:id') != null
  const divisionId = objectId ?? (isInstructorRoute ? undefined : id)

  const { data: userProfile } = useUserProfile()
  const currentRole = getCurrentRole(userProfile?.roles ?? [])
  const isPrimaryInstructor = currentRole?.roleName === 'Primary Instructor'

  const baseParams = { termId, distributionSeriesId: seriesId, pageSize: 200 }
  const { watch } = useFormContext<DisplaySettings>()
  const { fields, append, remove } = useFieldArray({
    control,
    name
  })
  const { setValue } = useFormContext<DisplaySettings>()

  const isSingleComparison = fields.length === 1
  const maxComparisons = 5

  const options = [
    ...(!isPrimaryInstructor ? [{ title: 'Instructor', value: 'instructor' }] : []),
    { title: 'Academic Unit', value: 'academic unit' }
  ]

  const getAddComparisonTooltip = (canAddMore: boolean): string => {
    if (canAddMore) return ''
    return 'You can only add up to 5 comparisons.'
  }

  const watchedComparisons = watch(name) || []
  const canAddMore = fields.length < maxComparisons

  return (
    <>
      <Typography color={black[100]} fontWeight={700}>Compare Data</Typography>

      {fields.map((field, index) => {
        const selectedComparisons = watchedComparisons
          .map((f, i) => (i !== index ? f.comparison : null))
          .filter(Boolean)
        const selectedComparisonOption = watchedComparisons[index]?.comparisonOption
        const compareByVal = watchedComparisons[index]?.compareBy
        const isAcademicCompare = compareByVal === 'academic unit'

        const compareBy = watchedComparisons[index]?.compareBy
        const isInstructorCompare = compareBy === 'instructor'
        // Academic unit search always uses the user-scoped endpoint — it handles all role
        // types (global, division-level, section/instructor) without requiring a valid
        // division UUID in the path, which avoids problems in instructor mode where the
        // URL :id param is a user UUID rather than a division UUID.
        const autocompleteEndpoint = isInstructorCompare
          ? `/api/divisions/${divisionId}/completion_rates/instructors/`
          : '/api/users/reports/search/'
        const autocompleteValueField = isInstructorCompare ? 'userId' : 'divisionId'

        return (
          <Box key={field.id} padding={theme.spacing(4, 4)} borderRadius={theme.spacing(2)} bgcolor={shade[750]} display="flex" flexDirection="column" gap={1.5} >
            <Typography color={grey[950]} fontWeight={500}>Compare By</Typography>
            <GenericAutocomplete
              key={`compareBy-${field.id}-${control._formValues?.[name]?.[index]?.compareBy ?? ''}`}
              name={`${name}.${index}.compareBy`}
              options={options}
              placeholder="Select Academic Unit or Instructor"
              getOptionValue={(option) => option?.value}
              getOptionLabel={(option) => option?.title}
              getOptionDisabled={(option) => option?.value === 'instructor' && !divisionId}
              textFieldStyles={{ bgcolor: 'white' }}
              onChange={() => {
                setValue(`${name}.${index}.comparison`, null)
                setValue(`${name}.${index}.comparisonOption`, null)
                setValue(`${name}.${index}.userId`, null)
              }}
            />

            <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>Select Comparison</Typography>
            <ApiAutoComplete
              key={`comparison-${field.id}-${compareBy ?? ''}-${control._formValues?.[name]?.[index]?.comparison ?? ''}`}
              name={`${name}.${index}.comparison`}
              apiEndpoint={autocompleteEndpoint}
              placeholder={isInstructorCompare ? 'Select Instructor' : 'Select Academic Unit or Instructor'}
              valueField={autocompleteValueField}
              labelField="title"
              apiParams={baseParams}
              getOptionDisabled={option => isInstructorCompare
                ? selectedComparisons.includes(option.userId)
                : selectedComparisons.includes(option.divisionId)}
              getOptionSubLabel={option => option.abbreviation ?? ''}
              selectedItem={selectedComparisonOption
                ? {
                    divisionId: selectedComparisonOption.id,
                    userId: selectedComparisonOption.id,
                    title: selectedComparisonOption.title,
                    abbreviation: selectedComparisonOption.abbreviation
                  }
                : undefined}
              wrapperStyle={{ minHeight: `${theme.spacing(15)} !important` }}
              clearIcon={false}
              textFieldStyles={{ bgcolor: 'white' }}
              skip={!compareByVal || (isInstructorCompare && !divisionId)}
              onChange={(option: ComparisonSearchOption | null) => {
                const resolvedId = option?.divisionId ?? option?.sectionId ?? option?.userId ?? option?.id ?? null
                // Explicitly set comparison so the schema validator always sees the value,
                // regardless of whether the Controller's onFieldChange fires correctly.
                setValue(`${name}.${index}.comparison`, resolvedId)
                setValue(
                  `${name}.${index}.comparisonOption`,
                  option?.title
                    ? {
                        id: String(resolvedId ?? ''),
                        title: option.title,
                        abbreviation: option.abbreviation ?? null
                      }
                    : null
                )
                if (isInstructorCompare) {
                  setValue(`${name}.${index}.userId`, option?.userId ?? null)
                } else {
                  setValue(`${name}.${index}.userId`, null)
                }
              }}
            />

            <Box display="flex" justifyContent="flex-end" width="100%" mt={-2} >
              <GenericButton
                buttonType={ButtonType.Ghost}
                buttonSize={ButtonSize.Small}
                startIcon={<Trash />}
                label={'Remove Comparison'}
                onClick={() => {
                  if (isSingleComparison) {
                    setValue(`${name}.0.compareBy`, null)
                    setValue(`${name}.0.comparison`, null)
                    setValue(`${name}.0.comparisonOption`, null)
                    setValue(`${name}.0.userId`, null)
                  } else {
                    remove(index)
                  }
                }}
                disabled={false}
                style={{
                  color: red[600],
                  fontWeight: 600,
                  backgroundColor: 'transparent',
                  padding: theme.spacing(1, 0)
                }}
              />
            </Box>
          </Box>
        )
      })}
      <Box display="flex" justifyContent="flex-end" >
        <Box display="flex" justifyContent="flex-end" >
          <CustomTooltip
            title={getAddComparisonTooltip(canAddMore)}
            placement="top"
          >
            <GenericButton
              buttonType={ButtonType.Ghost}
              buttonSize={ButtonSize.Small}
              label={'+ Add Comparison'}
              style={{ fontWeight: 600, fontSize: '16px', cursor: canAddMore ? 'pointer' : 'not-allowed' }}
              onClick={canAddMore ? () => { append({ compareBy: null, comparison: null, comparisonOption: null, userId: null }) } : undefined}
              disabled={!canAddMore}
            />
          </CustomTooltip>
        </Box>
      </Box>
    </>
  )
}

export default ComparisonsSection
